import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import {
  AUTH_INPUT,
  AuthCard,
  AuthEyebrow,
  AuthLayout,
  FormAlert,
  IconInput,
  StatusMark,
} from "@/components/auth/AuthLayout";
import { rise } from "@/components/auth/rise";
import { cn } from "@/lib/utils";

// =============================================================================
// /login
//
// The sign-in card, inside AuthLayout (the two-pane frame shared by every
// signed-out page: wordmarks, ambient backdrop, the Royal Sienna hero). Tabs
// for magic-link vs. password.
//
// All motion lives in index.css under "Login page" and switches off under
// prefers-reduced-motion. None of it touches the sign-in logic below.
// =============================================================================

type Mode = "magic" | "password";

interface LocationStateLike {
  from?: string;
  /** Set by ProtectedRoute when the user was bounced here by an expired
   *  session (vs. simply not being signed in yet). */
  reason?: string;
}

export function LoginPage() {
  return (
    <AuthLayout>
      <LoginForm />
    </AuthLayout>
  );
}

function LoginForm() {
  const [mode, setMode] = useState<Mode>("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSentTo, setMagicSentTo] = useState<string | null>(null);
  // Bumped on every submit so a repeated error re-mounts and shakes again.
  const [attempt, setAttempt] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const fromState = location.state as LocationStateLike | null;
  const redirectTo = fromState?.from ?? "/";
  const sessionExpired = fromState?.reason === "expired";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setAttempt((n) => n + 1);
    if (pending) return;

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError("Enter your work email.");
      return;
    }

    setPending(true);
    try {
      if (mode === "magic") {
        await api.auth.requestMagicLink({ email: trimmedEmail });
        setMagicSentTo(trimmedEmail);
        return;
      }

      if (!password) {
        setError("Enter your password.");
        return;
      }
      const res = await api.auth.login({ email: trimmedEmail, password });
      auth.hydrate(res.user);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(translateLoginError(err, mode));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <Greeting />
      <h1
        className="login-rise mb-2 text-4xl font-extrabold tracking-tight text-foreground sm:text-[2.75rem] sm:leading-[1.05] 2xl:mb-3 2xl:text-[3.6rem]"
        style={rise(110)}
      >
        Welcome back
      </h1>
      <p className="login-rise mb-7 text-[15px] text-foreground/70 [text-wrap:balance] 2xl:mb-9 2xl:text-[17px]" style={rise(170)}>
        Sign in to dispatch transports for Mount Sinai Medical Center.
      </p>
      {sessionExpired ? (
        <p
          role="status"
          className="login-rise mb-5 rounded-xl border border-amber-500/40 bg-amber-100/70 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100"
          style={rise(200)}
        >
          Your session expired for security. Sign in again to pick up where
          you left off.
        </p>
      ) : null}

      <AuthCard>
        {magicSentTo ? (
          <MagicSentScreen
            email={magicSentTo}
            onTryAgain={() => setMagicSentTo(null)}
          />
        ) : (
          <>
            <ModeSwitcher mode={mode} onChange={setMode} />

            <form onSubmit={onSubmit} className="grid gap-4" noValidate>
              <Field label="Email" htmlFor="email" required>
                <IconInput icon={<Mail className="h-4 w-4" aria-hidden="true" />}>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@mountsinai.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={200}
                    className={AUTH_INPUT}
                  />
                </IconInput>
              </Field>

              {mode === "password" ? (
                <div className="login-expand">
                  <div>
                    <Field
                      label="Password"
                      htmlFor="password"
                      required
                      hint={
                        <Link
                          to="/forgot-password"
                          className="text-primary hover:underline"
                        >
                          Forgot password? Send me a reset link.
                        </Link>
                      }
                    >
                      <IconInput icon={<Lock className="h-4 w-4" aria-hidden="true" />}>
                        <Input
                          id="password"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          maxLength={500}
                          className={AUTH_INPUT}
                        />
                      </IconInput>
                    </Field>
                  </div>
                </div>
              ) : null}

              {error ? <FormAlert key={attempt}>{error}</FormAlert> : null}

              <Button
                type="submit"
                size="lg"
                disabled={pending}
                className="login-cta mt-1 h-12 w-full rounded-2xl text-base 2xl:h-14"
              >
                {pending ? (
                  <>
                    <Spinner /> {mode === "magic" ? "Sending link…" : "Signing in…"}
                  </>
                ) : mode === "magic" ? (
                  <>
                    <Mail className="h-4 w-4" /> Email me a sign-in link
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" /> Sign in
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-5 text-center text-xs text-muted-foreground">
              Access is invitation-only. If you don't have an account, ask an administrator.
            </p>
          </>
        )}
      </AuthCard>
    </>
  );
}

/** "Good morning" / "Good afternoon" / "Good evening", from the viewer's own
 *  clock. A greeting only — it asserts nothing about the system. */
function Greeting() {
  const hour = new Date().getHours();
  const part =
    hour >= 5 && hour < 12
      ? "Good morning"
      : hour >= 12 && hour < 17
        ? "Good afternoon"
        : "Good evening";
  return <AuthEyebrow>{part}</AuthEyebrow>;
}

// =============================================================================
// Form interactions
// =============================================================================

function ModeSwitcher({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div
      data-mode={mode}
      className="login-switch mb-5 rounded-2xl border border-border/80 bg-foreground/[0.04] p-1"
    >
      <span aria-hidden="true" className="login-switch-pill" />
      <ModeTab active={mode === "magic"} onClick={() => onChange("magic")}>
        <Mail className="h-3.5 w-3.5" /> Magic Link
      </ModeTab>
      <ModeTab active={mode === "password"} onClick={() => onChange("password")}>
        <Lock className="h-3.5 w-3.5" /> Password
      </ModeTab>
    </div>
  );
}

function ModeTab({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "relative z-10 flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "inline-flex items-center justify-center gap-1.5",
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function MagicSentScreen({
  email,
  onTryAgain,
}: {
  email: string;
  onTryAgain: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="login-pop flex flex-col items-center gap-4 px-2 py-4 text-center"
    >
      <StatusMark tone="success" />
      <h2 className="text-xl font-bold text-foreground">Check your email</h2>
      <p className="text-sm text-muted-foreground">
        We sent a sign-in link to{" "}
        <strong className="font-semibold text-foreground">{email}</strong>. It
        expires in 15 minutes.
      </p>
      <p className="text-xs text-muted-foreground">
        Didn't get it? Check spam, then{" "}
        <button
          type="button"
          onClick={onTryAgain}
          className="font-semibold text-primary hover:underline"
        >
          try again
        </button>
        .
      </p>
    </div>
  );
}

function translateLoginError(err: unknown, mode: Mode): string {
  if (err instanceof ApiError) {
    if (err.code === "INVALID_CREDENTIALS") return "Wrong email or password.";
    if (err.code === "RATE_LIMITED") return "Too many attempts. Wait a minute and try again.";
    if (err.code === "INVALID_INPUT") {
      return mode === "password"
        ? "Email and password are required."
        : "Enter a valid email address.";
    }
    return err.message;
  }
  return "Something went wrong. Try again in a moment.";
}
